import * as React from 'react';

import {TimelineHeaderEmailSource} from 'shells/timeline/entry/header/timeline-header-email-source';
import type {Reaction, Reactions} from 'shells/timeline/reaction-bar/types';

import type {EventFragmentFull, SessionUser} from 'nutshell-graphql-types';

import {EmailEventActionMenu} from '../email-event-action-menu';
import {
    getActorForEmail,
    getAdditionalToolbarOptionsForEmail,
    getEmailIconVariant,
    getEntryTypeFromPayload,
    getParticipantsForAvatarList,
    getParticipantsForEmail,
    getStatusForEmail,
    getSubjectForEmail,
} from '../helpers';
import {getCommentsWithFilteredReactions} from '../reactions/helpers';

import {TimelineEntryModal} from './timeline-entry-modal';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    currentUser: SessionUser;
    event: EventFragmentFull;
    isLoading?: boolean;
    retryFailedEmail: (arg1: string) => Promise<any>;
    updateEmail: (arg1: string, arg2: boolean) => Promise<any>;
    onToggleReaction: (
        changeLogId: string,
        reactionType: Reaction,
        filteredReactions: Reactions,
        parentChangeLogId?: string
    ) => any;
    filteredEntryReactions: Reactions;
    onAddComment: (value: string) => Promise<any>;
    onDeleteComment: (commentId: string) => Promise<any>;
    onDeleteEvent: (arg1: string) => Promise<any>;
    onDeleteEmail: (emailId: string, allByAddress?: string | null | undefined) => any;
    isViewingAttachments: boolean;
    setIsViewingAttachments: (arg1: boolean) => void;
    onReply: () => void;
    onReplyAll: () => void;
};

export function TimelineEmailModal(props: Props) {
    const {
        event,
        isOpen,
        onClose,
        isLoading,
        retryFailedEmail,
        updateEmail,
        currentUser,
        onToggleReaction,
        filteredEntryReactions,
        onAddComment,
        onDeleteComment,
        onDeleteEvent,
        onDeleteEmail,
        isViewingAttachments,
        setIsViewingAttachments,
        onReply,
        onReplyAll,
    } = props;
    const {payload, id} = event;

    const deleteEmailAndEvent = (emailId: string, allByAddress?: string | null) => {
        // If removing all by address, we don't want to close the modal since we
        // want to continue into the blocklist page
        if (allByAddress) {
            return onDeleteEmail(emailId, allByAddress);
        }

        // Note: this is the changeLogId, not the emailId. Calling onClose
        // directly due to the modal not using an optimistic delete
        // @ts-expect-error
        return onDeleteEvent(id).then(onClose());
    };

    /**
     * We need to include this check, even though we are only passing an event
     * with a payload.__typename of 'Email' because Apollo doesn't generate a specific
     * enough type when using a union type (like the TimelineEventPayload).
     * See this issue for more detail:
     * https://github.com/apollographql/apollo-tooling/issues/1223
     */
    if (payload.__typename === 'Email') {
        const participants = getParticipantsForEmail(payload.recipients, payload.emailRelatedLeads);
        const actor = getActorForEmail(payload.from);

        let subtitleComponent = null;
        if (payload.originatedVia) {
            subtitleComponent = <TimelineHeaderEmailSource originatedVia={payload.originatedVia} />;
        }

        return (
            <TimelineEntryModal
                isOpen={isOpen}
                onClose={onClose}
                isLoading={isLoading}
                iconVariant={getEmailIconVariant(payload)}
                participantsForAvatarList={getParticipantsForAvatarList(
                    participants,
                    actor,
                    payload
                )}
                timestamp={event.changeTime}
                actor={actor}
                actorEmailAddress={payload.from.address}
                participants={participants}
                entryType={getEntryTypeFromPayload(payload)}
                changeType={event.changeType}
                subject={getSubjectForEmail(payload, true)}
                subjectTitle={payload.subject}
                content={payload.bodyExcerpt}
                htmlContent={payload.emailBodyHtml}
                status={getStatusForEmail(payload, retryFailedEmail, updateEmail)}
                filteredEntryReactions={filteredEntryReactions}
                currentUserId={currentUser ? currentUser.id : undefined}
                changeLogId={event.id}
                onToggleReaction={onToggleReaction}
                subtitleComponent={subtitleComponent}
                emailEngagement={{
                    attachments: payload.attachments,
                    clicks: payload.trackedClicksCount || 0,
                    opens: payload.trackedViewsCount || 0,
                }}
                comments={getCommentsWithFilteredReactions(event.comments)}
                onAddComment={onAddComment}
                onDeleteComment={onDeleteComment}
                actionMenu={
                    <EmailEventActionMenu
                        currentUser={currentUser}
                        changeLogId={event.id}
                        email={payload}
                        onDeleteEmail={deleteEmailAndEvent}
                        onUpdateEmail={updateEmail}
                        onRetryFailedEmail={retryFailedEmail}
                        isViewingAttachments={isViewingAttachments}
                        setIsViewingAttachments={setIsViewingAttachments}
                        onReply={onReply}
                        onReplyAll={onReplyAll}
                    />
                }
                additionalToolbarOptions={getAdditionalToolbarOptionsForEmail(payload, {
                    onReply,
                    onReplyAll,
                })}
            />
        );
    }

    return null;
}
